import { differenceInDays } from 'date-fns'
import { formatDistanceToNowStrict } from 'date-fns/formatDistanceToNowStrict'
import { enUS } from 'date-fns/locale/en-US'

import type {
	Card,
	ChainData,
	ChainDecentralizationData,
	ChainFinancialsData,
} from '#lib/models/index.js'
import { m } from '#lib/paraglide/messages.js'

import { getToken, isChainPos } from './chainsHelpers'
import type { chainSchema } from './schema'

export type Form = 'long' | 'short' | 'none'
export type Notation = 'standard' | 'compact'

const numberFormat = (options: Intl.NumberFormatOptions) => new Intl.NumberFormat('en-US', options)

const usd = (options: Intl.NumberFormatOptions) =>
	numberFormat({ style: 'currency', currency: 'USD', ...options })

const dateFormat = (options: Intl.DateTimeFormatOptions) =>
	new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', ...options })

const countFormatter = numberFormat({ maximumFractionDigits: 2 })
const compactFormatter = numberFormat({
	notation: 'compact',
	maximumSignificantDigits: 3,
})
const tinyFormatter = numberFormat({ maximumSignificantDigits: 3 })
const usdCompactFormatter = usd({ notation: 'compact', maximumSignificantDigits: 3 })
const dollarsFormatter = usd({ trailingZeroDisplay: 'stripIfInteger' })
const priceLargeFormatter = usd({ minimumFractionDigits: 2, maximumFractionDigits: 2 })
const priceMediumFormatter = usd({ minimumFractionDigits: 2, maximumFractionDigits: 4 })
const priceSmallFormatter = usd({ maximumSignificantDigits: 4 })
// Matches formatUsd's 3 significant digits once leading zeros are collapsed
const priceTinyFormatter = usd({ maximumSignificantDigits: 3 })
const percentFormatter = numberFormat({
	style: 'percent',
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
})
const percentTrimmedFormatter = numberFormat({ style: 'percent', maximumFractionDigits: 2 })

const fractionFormatters: Record<number, Intl.NumberFormat> = {}

const fraction = (digits: number) =>
	(fractionFormatters[digits] ??= numberFormat({ maximumFractionDigits: digits }))

const dateMediumFormatter = dateFormat({ dateStyle: 'medium' })

// hourCycle instead of `hour12: false`, which renders midnight as "24:00" in some engines
const timeTwoDigitFormatter = dateFormat({
	hour: '2-digit',
	minute: '2-digit',
	hourCycle: 'h23',
	timeZoneName: 'short',
})

const dateLongFormatter = dateFormat({
	weekday: 'short',
	year: 'numeric',
	month: 'short',
	day: 'numeric',
	hour: '2-digit',
	minute: '2-digit',
	second: '2-digit',
	hourCycle: 'h23',
	timeZoneName: 'short',
})

const isBlank = (value: Nullable<number>): value is null | undefined =>
	value == null || !Number.isFinite(value)

const SUBSCRIPT_DIGITS = '₀₁₂₃₄₅₆₇₈₉'

const toSubscript = (n: number) =>
	[...String(n)].map(digit => SUBSCRIPT_DIGITS[Number(digit)]).join('')

// Collapses 4+ leading fraction zeros (values below 0.0001): $0.00000000245 → $0.0₈245
export const subscriptZeros = (formatted: string) =>
	formatted.replace(/0\.(0{4,})(?=[1-9])/, (_, zeros: string) => `0.0${toSubscript(zeros.length)}`)

const lookup = <T>(table: Record<string, T>, key: Nullable<string>, fallback: T) =>
	key != null && Object.hasOwn(table, key) ? table[key] : fallback

const withUnit = (
	formatted: string,
	form: Form,
	short: () => string,
	long: () => string = short
) => (form === 'none' ? formatted : `${formatted} ${form === 'long' ? long() : short()}`)

export const formatCount = (value: Nullable<number>, notation: Notation = 'standard') =>
	isBlank(value) ? '—' : (notation === 'compact' ? compactFormatter : countFormatter).format(value)

const unitFormatter =
	<Args extends unknown[]>(
		format: (value: number, notation: Notation, ...args: Args) => string,
		defaultForm: Form,
		short: () => string,
		long?: () => string
	) =>
	(
		value: Nullable<number>,
		form: Form = defaultForm,
		notation: Notation = 'standard',
		...args: Args
	) =>
		isBlank(value) ? '—' : withUnit(format(value, notation, ...args), form, short, long)

const countWithUnit = (short: () => string) => unitFormatter(formatCount, 'none', short)

export const formatValidators = countWithUnit(() => m.metrics_validators())
export const formatMiners = countWithUnit(() => m.metrics_miners())
export const formatDevelopers = countWithUnit(() => m.metrics_developers())
export const formatRepos = countWithUnit(() => m.metrics_repos())
export const formatCommits = countWithUnit(() => m.metrics_commits())
export const formatStars = countWithUnit(() => m.metrics_stars())
export const formatWatchers = countWithUnit(() => m.metrics_watchers())
export const formatAgents = countWithUnit(() => m.metrics_agents())
export const formatServices = countWithUnit(() => m.metrics_services())

export const formatTxns = unitFormatter(
	formatCount,
	'short',
	() => m.metrics_txns_short(),
	() => m.metrics_txns_full()
)

export const formatValidatorsOrMiners = (
	value: Nullable<number>,
	chain: ChainDecentralizationData | ChainData
) => (isChainPos(chain) ? formatValidators(value) : formatMiners(value))

const formatTpsValue = (value: number, notation: Notation, digits = 2) => {
	const abs = Math.abs(value)

	if (abs === 0) return '0'

	if (abs < 0.0001) return subscriptZeros(tinyFormatter.format(value))

	if (abs >= 1000 && notation === 'compact') return compactFormatter.format(value)

	return fraction(abs >= 1000 ? 0 : abs >= 100 ? 1 : abs >= 0.01 ? digits : 4).format(value)
}

export const formatTps = unitFormatter(
	formatTpsValue,
	'short',
	() => m.metrics_tps_short(),
	() => m.metrics_tps_full()
)

export const formatHashrate = (value: Nullable<number>) =>
	isBlank(value) ? '—' : `${compactFormatter.format(value)} EH/s`

export const formatUsd = (value: Nullable<number>) =>
	isBlank(value) ? '—' : subscriptZeros(usdCompactFormatter.format(value))

// Same 'usd' | 'native' union for stake, revenue and avgTxFee
type AmountState = ReturnType<typeof chainSchema.parse>['stake']

const formatTokenAmount = (
	value: Nullable<number>,
	chain: Nullable<ChainData | ChainFinancialsData | ChainDecentralizationData>,
	state: AmountState,
	kind: 'stake' | 'gas'
) => {
	if (isBlank(value) || state === 'usd') return formatUsd(value)

	const formatted = subscriptZeros(compactFormatter.format(value))
	const token = getToken(chain, kind)

	return token != null ? `${formatted} ${token}` : formatted
}

export const formatStake = (
	value: Nullable<number>,
	chain: ChainDecentralizationData | ChainData,
	state: AmountState
) => formatTokenAmount(value, chain, state, 'stake')

export const formatStakeOrHashrate = (
	value: Nullable<number>,
	chain: ChainDecentralizationData | ChainData,
	state: AmountState
) => (isChainPos(chain) ? formatStake(value, chain, state) : formatHashrate(value))

export const formatRevenue = (
	value: Nullable<number>,
	chain?: ChainFinancialsData | ChainData,
	state: ReturnType<typeof chainSchema.parse>['revenue'] = 'usd'
) => formatTokenAmount(value, chain, state, 'gas')

export const formatAvgTxFee = (
	value: Nullable<number>,
	chain?: ChainFinancialsData | ChainData,
	state: ReturnType<typeof chainSchema.parse>['avgTxFee'] = 'usd'
) => formatTokenAmount(value, chain, state, 'gas')

export const formatPrice = (value: Nullable<number>) => {
	if (isBlank(value)) return '—'

	const abs = Math.abs(value)

	if (abs >= 1 || abs === 0) return priceLargeFormatter.format(value)

	if (abs < 0.0001) return subscriptZeros(priceTinyFormatter.format(value))

	return (abs >= 0.1 ? priceMediumFormatter : priceSmallFormatter).format(value)
}

export const formatChange = (value: Nullable<number>) =>
	percentFormatter.format(isBlank(value) ? 0 : Math.abs(value))

export const formatMarketCapToFdv = (value: Nullable<number>) =>
	isBlank(value) ? '—' : percentFormatter.format(value)

export const formatRatio = (ratio: number) => {
	if (Number.isNaN(ratio)) return '—'

	if (!Number.isFinite(ratio)) return '100%'

	if (ratio >= 1_000_000) return `${compactFormatter.format(ratio)}X`

	if (ratio >= 10) return `${fraction(0).format(ratio)}X`

	if (ratio > 1) return `${fraction(2).format(ratio)}X`

	if (ratio === 1) return '1X'

	// 2 fraction digits would round tiny non-zero ratios up to a misleading 100%
	if (ratio > 0 && 1 - ratio > 0.9999) return '>99.99%'

	return percentTrimmedFormatter.format(1 - ratio)
}

export const getRelation = (
	firstValue: Nullable<number>,
	secondValue: Nullable<number>
): [number, number] => {
	if (firstValue == null && secondValue == null) return [0, 0]

	if (firstValue == null) return [0, 100]

	if (secondValue == null) return [100, 0]

	if (firstValue > secondValue) return [100, (secondValue * 100) / firstValue]

	if (firstValue < secondValue) return [(firstValue * 100) / secondValue, 100]

	return [100, 100]
}

export const getRatio = (
	firstValue: Nullable<number>,
	secondValue: Nullable<number>,
	inverse = false
) => {
	if (firstValue == null && secondValue == null) return 1

	if (firstValue == null) return inverse === true ? Infinity : -Infinity

	if (secondValue == null) return inverse === true ? 0 : Infinity

	if (firstValue === secondValue) return 1

	return firstValue / secondValue
}

const TIME_UNITS = [
	{ seconds: 86400, short: 'd', one: 'day', many: 'days' },
	{ seconds: 3600, short: 'h', one: 'hour', many: 'hours' },
	{ seconds: 60, short: 'm', one: 'min', many: 'min' },
	{ seconds: 1, short: 's', one: 'sec', many: 'sec' },
] as const

const pickTimeUnit = (seconds: number) => TIME_UNITS.findIndex(unit => seconds >= unit.seconds)

const formatTimeUnit = (count: number, unit: (typeof TIME_UNITS)[number], long: boolean) =>
	long
		? `${fraction(0).format(count)} ${count === 1 ? unit.one : unit.many}`
		: `${fraction(0).format(count)}${unit.short}`

const formatDuration = (seconds: number, long: boolean) => {
	const step = TIME_UNITS[pickTimeUnit(Math.round(seconds)) + 1].seconds
	const total = Math.round(seconds / step) * step

	const index = pickTimeUnit(total)
	const major = TIME_UNITS[index]
	const minor = TIME_UNITS[index + 1]

	const majorCount = Math.floor(total / major.seconds)
	const minorCount = Math.floor((total - majorCount * major.seconds) / minor.seconds)

	return [formatTimeUnit(majorCount, major, long)]
		.concat(minorCount > 0 ? formatTimeUnit(minorCount, minor, long) : [])
		.join(' ')
}

export const formatTime = (value: Nullable<number>, long = false) => {
	if (isBlank(value)) return '—'

	const sign = value < 0 ? '-' : ''
	const abs = Math.abs(value)
	const ms = Math.round(abs * 1000)

	if (ms > 0 && ms < 1000) return `${sign}${ms}ms`

	const seconds = fraction(2).format(abs)

	if (abs < 60 && seconds !== '60') return `${sign}${seconds}${long ? ' sec' : 's'}`

	return `${sign}${formatDuration(abs, long)}`
}

/** Stopwatch reading, ex. `01:02:03` */
export const formatElapsed = (ms: number) => {
	const seconds = Math.floor(ms / 1000)

	return [Math.floor(seconds / 3600), Math.floor(seconds / 60) % 60, seconds % 60]
		.map(part => String(part).padStart(2, '0'))
		.join(':')
}

/** Time to finality: chains that finalize with the block are stored as 0 */
export const formatFinality = (value: Nullable<number>, long = false) =>
	value === 0 ? m.metric_ttf_instant() : formatTime(value, long)

export const formatLaunchDate = (
	value: Nullable<Date>,
	state: ReturnType<typeof chainSchema.parse>['launched']
) => {
	if (value == null) return '—'

	return state === 'date'
		? dateMediumFormatter.format(value)
		: formatDistanceToNowStrict(value, { locale: enUS, addSuffix: true })
}

export const formatCalculated = (value: Nullable<Date>) =>
	value != null ? dateLongFormatter.format(value) : '—'

export const getUtcDateTimeString = (date = new Date()) =>
	`${dateMediumFormatter.format(date)}, ${timeTwoDigitFormatter.format(date)}`

export const formatCreatedAt = (value: Nullable<Date>) => {
	if (value == null) return '—'

	return differenceInDays(new Date(), value) <= 7
		? formatDistanceToNowStrict(value, { locale: enUS, addSuffix: true })
		: dateMediumFormatter.format(value)
}

const RANGES = {
	hour: '1H',
	day: '1D',
	week: '7D',
	month: '30D',
	'all-time': 'All Time',
} as const satisfies Record<string, string>

export const formatRange = (value: Nullable<string>) => lookup(RANGES, value, '1D')

export const formatMetricName = (
	range: Nullable<string>,
	name: () => string,
	nameRange: (params: { range: string }) => string
) => (range === 'all-time' ? name() : nameRange({ range: formatRange(range) }))

const TYPES: Record<string, [long: () => string, short: () => string]> = {
	layer_1: [m.metrics_type_l1_long, m.metrics_type_l1_short],
	layer_2: [m.metrics_type_l2_long, m.metrics_type_l2_short],
	ecosystem: [m.metrics_type_ecosystem_long, m.metrics_type_ecosystem_short],
	sidechain: [m.metrics_type_sidechain, m.metrics_type_sidechain],
}

export const formatType = (value: Nullable<string>, isLong = true) =>
	lookup(TYPES, value, [() => '—', () => '—'])[isLong ? 0 : 1]()

const GOVERNANCE = {
	on_chain: 'On-chain',
	off_chain: 'Off-chain',
	council: 'Council',
	multisig: 'Multisig',
} as const satisfies Record<string, string>

export const formatGovernance = (value: Nullable<string>) => lookup(GOVERNANCE, value, '—')

const same = (name: string): [string, string] => [name, name]

const CONSENSUS: Record<string, [long: string, short: string]> = {
	pos: ['Proof of Stake (PoS)', 'PoS'],
	pow: ['Proof of Work (PoW)', 'PoW'],
	ppos: same('Pure Proof of Stake'),
	npos: same('Nominated Proof of Stake'),
	por: same('Proof of Responsibility'),
	pouw: same('Proof of Useful Work'),
	dpos: same('Delegated Proof of Stake'),
	scp: same('Stellar Consensus Protocol'),
	op: same('Rollup (Optimistic)'),
	zk: same('Rollup (ZK)'),
	posa: same('Proof of Staked Authority'),
}

export const formatConsensus = (value: Nullable<string>, form: 'long' | 'short' = 'long') =>
	lookup(CONSENSUS, value, same('—'))[form === 'long' ? 0 : 1]

export const formatAddress = (address: string) => {
	const match = address.match(/^(0x[a-zA-Z0-9]{4})[a-zA-Z0-9]+([a-zA-Z0-9]{4})$/)

	return match ? `${match[1]}…${match[2]}` : address
}

const formatPoints = (value: number) => `${countFormatter.format(value)}%`

const CARD_TYPES = {
	exchange: 'Exchange',
	neobank: 'Neobank',
	defi: 'DeFi',
} as const satisfies Record<string, string>

export const formatCardType = (value: string) => lookup(CARD_TYPES, value, value)

const CARD_NETWORKS = { visa: 'Visa', mastercard: 'Mastercard' } as const satisfies Record<
	string,
	string
>

export const formatCardNetwork = (value: string) => lookup(CARD_NETWORKS, value, value)

export const formatCardCashback = ({ min, max, limit }: Card['cashback']) => {
	if (min === 0 && max === 0) return 'No cashback'

	let formattedLimit = ''

	if (limit === 0) {
		formattedLimit = ' (No limit)'
	} else if (limit != null) {
		formattedLimit = ` (Up to ${dollarsFormatter.format(limit)})`
	}

	if (min === 0) return `Up to ${formatPoints(max)}${formattedLimit}`

	return `${countFormatter.format(min)} – ${formatPoints(max)}${formattedLimit}`
}

export const formatCardAnnualFee = (value: Card['annualFee']) =>
	value === 0 ? 'No fee' : dollarsFormatter.format(value)

export const formatCardFxFee = (value: Card['fxFee']) =>
	value === 0 ? 'No fee' : formatPoints(value)

export const formatCardKyc = (value: Card['kyc']) => (value ? 'Required' : 'Not required')

export const formatCardBorrowing = (value: Card['borrowing']) =>
	value ? 'Available' : 'Not available'
